import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Plus, Minus, ArrowRight, ArrowLeft, Check, LayoutList, UserPlus, Sparkles, Loader2, Square, BookmarkPlus, Trash2, Share2 } from "lucide-react";
import { cn, formatCurrency } from "@/lib/utils";
import VarianceModal from "@/components/VarianceModal";
import SessionSummaryModal from "@/components/SessionSummaryModal";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import type { NewSessionViewProps } from "@/ui/view-props";

export function NewSessionClassicView(p: NewSessionViewProps) {
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [deleteTemplateId, setDeleteTemplateId] = useState<string | null>(null);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const totalPot = p.sessionPlayers.reduce((sum, r) => sum + r.buyIns * p.buyInAmount, 0);
  const totalWon = p.sessionPlayers.reduce((sum, r) => {
    const net = (parseFloat(r.cashOut) || 0) - r.buyIns * p.buyInAmount;
    return net > 0 ? sum + net : sum;
  }, 0);

  if (p.step === 1) {
    return (
      <div className="py-6 space-y-4 pb-32">
        <h1 className="text-xl font-bold tracking-tight">Select Players</h1>
        <p className="text-sm text-muted-foreground">Choose who's playing tonight</p>

        <div className="space-y-2">
          {p.players.map((player: any) => (
            <Card
              key={player.id}
              role="listitem"
              data-testid={`player-select-${player.name}`}
              className={cn(
                "border-0 shadow-sm cursor-pointer transition-all scroll-mb-32",
                p.selectedIds.has(player.id) && "ring-2 ring-primary"
              )}
              onClick={() => p.togglePlayer(player.id)}
            >
              <CardContent className="p-3 flex items-center gap-3">
                <Checkbox checked={p.selectedIds.has(player.id)} />
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-primary">
                    {player.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-sm">{player.name}</p>
                  {player.nickname && (
                    <p className="text-xs text-muted-foreground">"{player.nickname}"</p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Session Templates */}
        {p.showTemplatesSection && (
          <div className="space-y-2">
            {(p.templates ?? []).length > 0 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">Templates</p>
                <div className="flex flex-wrap gap-2">
                  {(p.templates ?? []).map(tmpl => (
                    <div key={tmpl.id} className="flex items-center gap-1 border rounded-full px-3 py-1 text-xs font-medium bg-muted/50">
                      <button type="button" onClick={() => p.onLoadTemplate?.(tmpl)} className="hover:text-primary transition-colors">
                        {tmpl.name}
                      </button>
                      <button type="button" onClick={() => setDeleteTemplateId(tmpl.id)} className="ml-1 text-muted-foreground hover:text-destructive">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {showSaveTemplate ? (
              <div className="flex gap-2">
                <Input
                  placeholder="Template name (e.g. Friday Crew)"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="h-9 text-sm"
                  autoFocus
                />
                <Button
                  size="sm"
                  disabled={!templateName.trim() || savingTemplate}
                  onClick={async () => {
                    setSavingTemplate(true);
                    await p.onSaveTemplate?.(templateName.trim());
                    setTemplateName('');
                    setShowSaveTemplate(false);
                    setSavingTemplate(false);
                  }}
                >
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setShowSaveTemplate(false); setTemplateName(''); }}>
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="gap-2 w-full"
                disabled={p.selectedIds.size < 2}
                onClick={() => setShowSaveTemplate(true)}
              >
                <BookmarkPlus className="h-4 w-4" /> Save as Template
              </Button>
            )}
          </div>
        )}

        {p.showAddPlayer ? (
          <div className="flex gap-2">
            <Input
              placeholder="Player name"
              value={p.newPlayerName}
              onChange={(e) => p.setNewPlayerName(e.target.value)}
              className="h-11"
              autoFocus
            />
            <Button onClick={p.addPlayerInline} className="h-11">
              Add
            </Button>
            <Button
              variant="ghost"
              onClick={() => p.setShowAddPlayer(false)}
              className="h-11"
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            className="w-full gap-2"
            onClick={() => p.setShowAddPlayer(true)}
          >
            <UserPlus className="h-4 w-4" /> Add New Player
          </Button>
        )}

        <ConfirmDialog
          open={deleteTemplateId !== null}
          onOpenChange={(open) => { if (!open) setDeleteTemplateId(null); }}
          title="Delete Template"
          description="Are you sure you want to delete this template? This cannot be undone."
          confirmLabel="Delete"
          onConfirm={() => { if (deleteTemplateId) p.onDeleteTemplate?.(deleteTemplateId); setDeleteTemplateId(null); }}
        />

        {/* Buy-in amount picker */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Buy-in amount</Label>
          <div className="flex gap-2 flex-wrap items-center">
            {[10, 20, 50, 100].map((preset) => (
              <Button
                key={preset}
                size="sm"
                variant={p.buyInUnit === preset ? 'default' : 'outline'}
                onClick={() => p.setBuyInUnit(preset)}
                data-testid={`buyin-preset-${preset}`}
              >
                {preset}
              </Button>
            ))}
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              value={p.buyInUnit}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (v > 0) p.setBuyInUnit(v);
              }}
              className="w-24 h-9"
              data-testid="buyin-custom-input"
            />
          </div>
        </div>

        <div className={cn(
          "sticky bottom-16 z-40 bg-background/80 backdrop-blur-sm p-4 border-t -mx-4 mt-4",
          "sticky-mobile-footer"
        )}>
          <Button
            className="w-full h-14 text-base font-semibold gap-2"
            onClick={p.startSession}
            disabled={p.selectedIds.size < 2}
          >
            Start Session <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
        </div>
        );
        }

  if (p.step === 2) {
    return (
      <div className="py-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold tracking-tight">Active Session</h1>
            {p.elapsedTime && (
              <p className="text-xs text-muted-foreground font-mono mt-0.5">⏱ {p.elapsedTime}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {p.onShareLiveLink && (
              <Button
                size="sm"
                variant="outline"
                className="gap-1 text-xs"
                onClick={p.onShareLiveLink}
                data-testid="share-live-btn"
                title="Copy live scoreboard link"
              >
                <Share2 className="h-3.5 w-3.5" /> Share Live
              </Button>
            )}
            <Button
              size="sm"
              variant={p.roastSpeaking ? "destructive" : "outline"}
              className="gap-1.5 text-xs"
              disabled={p.roastGenerating}
              onClick={p.onRoastSession}
              data-testid="roast-session-btn"
            >
              {p.roastGenerating ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : p.roastSpeaking ? (
                <Square className="h-3.5 w-3.5" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
              {p.roastGenerating ? "Roasting…" : p.roastSpeaking ? "Stop" : "AI Roast"}
            </Button>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Total Pot</p>
              <p className="text-xl font-bold font-mono text-primary">{formatCurrency(totalPot)}</p>
            </div>
          </div>
        </div>

        {p.roast && (
          <Alert className="bg-primary/5 border-primary/20 animate-in fade-in slide-in-from-top-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <AlertDescription className="text-sm italic leading-relaxed">
              {p.roast}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          {p.sessionPlayers.map((sp) => (
            <Card key={sp.playerId} className="border-0 shadow-sm" role="listitem" data-testid={`player-row-${sp.name}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold">{sp.name}</p>
                    {sp.nickname && (
                      <p className="text-xs text-muted-foreground">"{sp.nickname}"</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="font-mono font-bold text-lg">
                      {sp.buyIns * p.buyInAmount}
                    </p>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => p.removePlayerFromSession(sp.playerId)}
                      data-testid={`remove-player-${sp.name}`}
                      title="Remove player from session"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-12 w-12 rounded-xl"
                    onClick={() => p.removeBuyIn(sp.playerId)}
                    disabled={sp.buyIns <= 1}
                    data-testid="remove-buy-in"
                  >
                    <Minus className="h-5 w-5" />
                  </Button>
                  <div className="flex-1 text-center">
                    <p className="text-2xl font-bold font-mono">{sp.buyIns}</p>
                    <p className="text-xs text-muted-foreground">entries</p>
                  </div>
                  <Button
                    size="icon"
                    className="h-12 w-12 rounded-xl"
                    onClick={() => p.addBuyIn(sp.playerId)}
                    data-testid="add-buy-in"
                  >
                    <Plus className="h-5 w-5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Confirm remove dialog */}
        <ConfirmDialog
          open={!!p.confirmRemovePlayerId}
          onOpenChange={(open) => { if (!open) p.cancelRemovePlayer(); }}
          title="Remove player?"
          description={`This player has ${p.sessionPlayers.find(sp => sp.playerId === p.confirmRemovePlayerId)?.buyIns ?? 0} buy-in(s). Remove them from the session entirely?`}
          confirmLabel="Remove"
          onConfirm={p.confirmRemovePlayer}
        />

        <div className="space-y-2">
          <p className="text-sm text-muted-foreground font-medium">Add player mid-session:</p>
          {p.availableMidSession.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {p.availableMidSession.map((pl: any) => (
                <Button
                  key={pl.id}
                  variant="outline"
                  size="sm"
                  onClick={() => p.addPlayerMidSession(pl)}
                >
                  <Plus className="h-3 w-3 mr-1" /> {pl.name}
                </Button>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Input
              placeholder="New player name…"
              value={p.midSessionNewPlayerName}
              onChange={(e) => p.setMidSessionNewPlayerName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') p.addNewPlayerMidSession(p.midSessionNewPlayerName);
              }}
              className="h-9 text-sm"
              data-testid="mid-session-new-player-input"
            />
            <Button
              size="sm"
              variant="secondary"
              className="gap-1 shrink-0"
              disabled={!p.midSessionNewPlayerName.trim()}
              onClick={() => p.addNewPlayerMidSession(p.midSessionNewPlayerName)}
              data-testid="mid-session-new-player-add"
            >
              <UserPlus className="h-3.5 w-3.5" /> Add
            </Button>
          </div>
        </div>

        <div className={cn(
          "sticky bottom-16 z-40 bg-background/80 backdrop-blur-sm p-4 border-t -mx-4 mt-4",
          "sticky-mobile-footer"
        )}>
          <Button
            className="w-full h-14 text-base font-semibold gap-2"
            variant="default"
            onClick={() => p.setStep(3)}
            data-testid="advance-to-cashout"
          >
            End Session <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1 min-w-0">
          <Button size="icon" variant="ghost" onClick={() => p.setStep(2)} className="shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold tracking-tight truncate">Results</h1>
        </div>
        <div className="text-right flex gap-3 shrink-0">
          <div>
            <p className="text-[10px] text-muted-foreground uppercase font-bold">Total Won</p>
            <p className="text-sm font-bold font-mono text-success">{formatCurrency(totalWon)}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground uppercase font-bold">Total Pot</p>
            <p className="text-sm font-bold font-mono text-primary">{formatCurrency(totalPot)}</p>
          </div>
        </div>
      </div>
      <p className="text-sm text-muted-foreground">Enter each player's final count</p>

      <div className="space-y-2">
        {p.sessionPlayers.map((sp) => {
          const cashOut = parseFloat(sp.cashOut) || 0;
          const net = cashOut - sp.buyIns * p.buyInAmount;
          const hasValue = sp.cashOut !== "";

          return (
            <Card key={sp.playerId} className="border-0 shadow-sm" role="listitem" data-testid={`player-row-${sp.name}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{sp.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {sp.buyIns} entr{sp.buyIns > 1 ? "ies" : "y"} ={" "}
                      {sp.buyIns * p.buyInAmount}
                    </p>
                  </div>
                  {hasValue && (
                    <span
                      className={cn(
                        "text-lg font-bold font-mono",
                        net > 0
                          ? "text-green-600"
                          : net < 0
                            ? "text-red-500"
                            : "text-muted-foreground"
                      )}
                    >
                      {net >= 0 ? "+" : ""}
                      {formatCurrency(net)}
                    </span>
                  )}
                </div>
                <div className="relative">
                  <LayoutList className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="number"
                    inputMode="decimal"
                    placeholder="Results amount"
                    value={sp.cashOut}
                    onChange={(e) => p.updateCashOut(sp.playerId, e.target.value)}
                    className="h-12 pl-9 font-mono text-lg"
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className={cn(
        "sticky bottom-16 z-40 bg-background/80 backdrop-blur-sm p-4 border-t -mx-4 mt-4",
        "sticky-mobile-footer"
      )}>
        <Button
          className="w-full h-14 text-base font-semibold gap-2"
          onClick={p.attemptEndSession}
          disabled={p.saving || p.sessionPlayers.some((sp) => sp.cashOut === "")}
          data-testid="finalize-session"
        >
          <Check className="h-5 w-5" />
          End Session
        </Button>
      </div>

      <VarianceModal
        open={p.showVarianceModal}
        onClose={() => p.setShowVarianceModal(false)}
        variance={p.varianceAmount}
        totalBuyIns={p.sessionPlayers.reduce((s, r) => s + r.buyIns * p.buyInAmount, 0)}
        totalCashOuts={p.sessionPlayers.reduce((s, r) => s + (parseFloat(r.cashOut) || 0), 0)}
        sessionPlayers={p.sessionPlayers}
        onSplitEqually={(adj) => p.handleVarianceResolved(adj, 'split')}
        onAssignToPlayer={(adj) => p.handleVarianceResolved(adj, 'manual')}
        onReenter={() => p.setShowVarianceModal(false)}
      />

      <SessionSummaryModal
        open={p.showSummaryModal}
        results={p.sessionPlayers.map((sp) => ({
          name: sp.name,
          buyIns: sp.buyIns,
          cashOut: parseFloat(sp.cashOut) || 0,
          net: (parseFloat(sp.cashOut) || 0) - sp.buyIns * p.buyInAmount,
        }))}
        onConfirm={p.endSession}
        saving={p.saving}
        buyInUnit={p.buyInUnit}
        sessionId={p.sessionId}
      />
    </div>
  );
}

